import { FilePdfOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Flex, message, Skeleton, Typography } from 'antd'
import type { GenericPanelData } from 'features/drilldown'
import { TooltipParagraph } from 'features/drilldown/components/TooltipParagraph'
import { DrilldownDataContext } from 'features/drilldown/data/savedExplorationState/DrilldownExplorationContext'
import { useNiiPdfExportScope } from 'features/drilldown/panels/impl/niiPdfExport/useNiiPdfExportScope'
import { useBulkNiiPdfExport } from 'features/libra/stepComponents/niiReport/pdf/useBulkNiiPdfExport'
import { type FC, type ReactNode, useContext } from 'react'
import styled from 'styled-components'

const { Text } = Typography

export const NII_PDF_EXPORT_TITLE_TOOLTIP: ReactNode = (
  <>
    <TooltipParagraph>
      Downloads the NII report PDF for each asset in the current scope &mdash;
      the latest <b>complete</b> analysis only, one file per asset.
    </TooltipParagraph>
    <TooltipParagraph>
      Narrow it with a filter panel above to export a subset.
    </TooltipParagraph>
  </>
)

export type NiiPdfExportPanelData = GenericPanelData

export const LATEST_NII_PDF_EXPORT_PANEL_DATA_SCHEMA_VERSION = 1

export const DEFAULT_NII_PDF_EXPORT_PANEL_DATA: NiiPdfExportPanelData = {
  panelDataSchemaVersion: LATEST_NII_PDF_EXPORT_PANEL_DATA_SCHEMA_VERSION,
  title: 'NII PDF Reports',
  isPinned: false,
}

/** Above this many reports, warn before the user commits to the wait. */
const LARGE_EXPORT_WARNING_THRESHOLD = 25

const Container = styled(Flex)`
  padding: 1rem;
  height: 100%;
  overflow-y: auto;
`

const TicketList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  max-height: 12rem;
  overflow-y: auto;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;

  li {
    margin-bottom: 0.125rem;
  }
`

/**
 * Bulk NII report export for the current drilldown scope.
 *
 * There is deliberately no ticket picker here: the export follows whatever
 * the Explorer is already showing (cascading filters from panels above, plus
 * any focused rows), so narrowing it is done with the filter panel like
 * every other panel. One report is saved per asset, latest version only.
 */
export const NiiPdfExportPanel: FC = () => {
  const { orgId } = useContext(DrilldownDataContext)
  const { tickets, workflowMap, ineligibleCount, isLoading } =
    useNiiPdfExportScope()
  const [messageApi, contextHolder] = message.useMessage()

  const {
    generateAndDownload,
    isGenerating,
    progress,
    isLoading: signoffsLoading,
  } = useBulkNiiPdfExport(tickets, workflowMap, orgId ?? '')

  const handleExport = async (): Promise<void> => {
    try {
      const { succeeded, failed } = await generateAndDownload()

      if (succeeded.length > 0 && failed.length === 0) {
        messageApi.success(
          `${succeeded.length} PDF${succeeded.length > 1 ? 's' : ''} saved to your downloads folder`
        )
      } else if (succeeded.length > 0) {
        messageApi.warning(
          `${succeeded.length} of ${succeeded.length + failed.length} PDFs saved. Failed: ${failed
            .map((entry) => entry.ticket.title)
            .join(', ')}`
        )
      } else {
        messageApi.error('Failed to generate any PDFs. Please try again.')
      }
    } catch (error) {
      console.error('Error generating NII PDF reports:', error)
      messageApi.error('Failed to export PDF reports. Please try again.')
    }
  }

  if (isLoading) return <Skeleton active paragraph={{ rows: 3 }} />

  if (tickets.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          ineligibleCount > 0
            ? `No exportable NII reports in scope — ${ineligibleCount} ticket${ineligibleCount > 1 ? 's have' : ' has'} no NII Report step`
            : 'No completed NII tickets in the current scope'
        }
      />
    )
  }

  return (
    <Container vertical gap='small'>
      {contextHolder}
      <Text>
        <b>{tickets.length}</b> NII report{tickets.length > 1 ? 's' : ''} in the
        current scope
      </Text>

      <TicketList>
        {tickets.map((ticket) => (
          <li key={ticket.id}>{ticket.title}</li>
        ))}
      </TicketList>

      {ineligibleCount > 0 && (
        <Alert
          type='info'
          showIcon
          message={`${ineligibleCount} ticket${ineligibleCount > 1 ? 's' : ''} in scope skipped — no NII Report step`}
        />
      )}

      {tickets.length > LARGE_EXPORT_WARNING_THRESHOLD && (
        <Alert
          type='warning'
          showIcon
          message={`You're about to generate ${tickets.length} PDFs at once`}
          description='Large exports take a while and save one file at a time — narrow the scope with a filter panel if you only need a subset.'
        />
      )}

      <Flex align='center' gap='small'>
        <Button
          type='primary'
          icon={<FilePdfOutlined />}
          loading={isGenerating}
          disabled={!orgId || signoffsLoading}
          onClick={() => void handleExport()}>
          Download PDF Reports
        </Button>
        {progress && (
          <Text type='secondary'>
            Generating {progress.done} of {progress.total}…
          </Text>
        )}
      </Flex>
    </Container>
  )
}
